'use strict';
/* ==========================================================================
   COPY for the `heat` lesson (DESIGN.md §4): lessons/energy/core-local/tracks.js, copied 2026-10-01, UNCHANGED
   (same hills, masses, μ and brake runs). The heat lesson uses the `s3` run (The Big Drop with brakes); the other
   entries are kept so the file stays a byte-for-byte copy of the physics data. Read-only helpers over a run
   (total, final, heatAlong) live at the end of core-local/coaster.js.
   ==========================================================================
   core-local/tracks.js — every coaster the lesson shows, in one place, so the scenes
   draw exactly what tools/energycheck.js proves.

     const R = Tracks.get('s3');      // built once, cached
     R.track                          // Coaster.Track (metres)
     R.sim                            // the primary Coaster.Sim; R.sims.<name> for twin tracks
     R.sim.sample(t)                  // {x, y, s, v, PE, KE, heat, total, ...} — draw from this
     R.clip                           // Spot clips only: {dur, window: [t0, t1] | null} in clip seconds
     R.marks                          // handy x positions / times (crest, bottom, ...) for scene timing

   Heights and masses come from content/script.json and content/spot.json; the
   horizontal spacing and μ are tuned so the stated behaviour really happens
   (see the notes on each entry). Clip time = sim time (t = 0 at release).
   ========================================================================== */
(function (root) {
  const C = (typeof window === 'undefined' && typeof require === 'function') ? require('./coaster.js') : root.Coaster;

  /* cosine-smoothed hill control points: every point is a flat crest/valley at exactly h */
  const DEFS = {
    /* s1–s3: THE BIG DROP. Flat station, 20 m chain-lift hill (8 → 30), a short crest
       plateau where the cart "sits" (30 → 32), the 20 m drop to the bottom at x = 56,
       a 12 m camel-back, then the brake run. Frictionless. */
    bigdrop: {
      hills: [{ x: 0, h: 0 }, { x: 8, h: 0 }, { x: 30, h: 20 }, { x: 32, h: 20 }, { x: 56, h: 0 },
        { x: 76, h: 12 }, { x: 94, h: 1 }, { x: 150, h: 1 }],
      marks: { liftStart: 8, liftTop: 30, crest: 32, bottom: 56, camel: 76 },
    },
    /* s2: the chain lift. 2 m/s up the lift (15.4 s), then parked at rest on top (chain hold):
       PE = 100,000 J, KE = 0. The chain's work is booked as sample.work. R.marks.tTop = arrival. */
    s2: {
      track: 'bigdrop',
      sim: { m: 500, mu: 0, x0: 8, v0: 0, chain: { x0: 8, x1: 30, speed: 2, hold: true }, tMax: 20 },
    },
    /* s3: the drop, from rest at the lip of the drop (a 0.3 m/s nudge so it leaves the crest; h0: the
       nudge is paid for by starting 4.5 mm down the lip, so the total is exactly 100,000 J — Sim.release). */
    s3: {
      track: 'bigdrop',
      sim: { m: 500, mu: 0, x0: 32, v0: 0.3, h0: 20, brakes: { x0: 110, x1: 150, mu: 0.6 }, tMax: 16 },
    },
    /* s4 / c3: two identical 20 m drops, frictionless. Bottom at x = 28. */
    twin: {
      hills: [{ x: 0, h: 20 }, { x: 4, h: 20 }, { x: 28, h: 0 }, { x: 115, h: 0 }],
      marks: { crest: 4, bottom: 28 },
    },
    s4: {
      track: 'twin',
      sims: {
        max: { m: 50, mu: 0, x0: 4, v0: 0.3, h0: 20, brakes: { x0: 50, x1: 115, mu: 0.8 }, tMax: 12 },
        elephant: { m: 5000, mu: 0, x0: 4, v0: 0.3, h0: 20, brakes: { x0: 50, x1: 115, mu: 0.8 }, tMax: 12 },
      },
    },
    /* s6: the real world. Hills 20 / 15 / 11 / 8 m with rolling friction, a flat valley,
       then the 25 m challenge hill. μ = 0.08 (booked as heat, energy height falls by μ per
       metre of x): it clears 15, 11 and 8 with margin, climbs the challenge hill to ~7.5 m
       (lower than every hill before it), rolls back and settles on the flat valley floor. */
    s6: {
      hills: [{ x: 0, h: 20 }, { x: 4, h: 20 }, { x: 28, h: 1 }, { x: 48, h: 15 }, { x: 66, h: 1 }, { x: 84, h: 11 },
        { x: 99, h: 1 }, { x: 113, h: 8 }, { x: 127, h: 1 }, { x: 155, h: 1 }, { x: 180, h: 25 }, { x: 204, h: 2 }, { x: 222, h: 2 }],
      marks: { crests: [4, 48, 84, 113], challenge: 180, valley: [127, 155] },
      // start just past the lip, where the slope beats friction (tanθ > μ), from rest
      sim: { m: 500, mu: 0.08, x0: 4.6, h0: 20, tMax: 70 },
    },

    /* ---------------- Spot the Mistake (content/spot.json) ---------------- */
    /* c1 HIGHER GROUND: 20 m hill, then a 25 m hill. Cheat: on hill 2 gravity only bites
       at 60 % (it "feels" 15 m tall), so the cart sails over the 25 m top. */
    c1: {
      hills: [{ x: 0, h: 20 }, { x: 3, h: 20 }, { x: 25, h: 0 }, { x: 38, h: 0 }, { x: 62, h: 25 }, { x: 86, h: 0 }, { x: 140, h: 0 }],
      marks: { crest: 3, hill2: 62 },
      sim: { m: 500, mu: 0, x0: 3, v0: 0.5, h0: 20, brakes: { x0: 105, x1: 140, mu: 0.8 }, tMax: 14,
        cheat: { type: 'extraEnergy', x0: 38, x1: 86, scale: 0.6 } },
      clip: {
        dur: 12,
        window: R => { const a = R.sim.when(p => p.x > 38 && p.y > 20); const b = R.sim.timeAtX(62); return [a, b + 2]; },
      },
    },
    /* c2 FRICTIONLESS*: 10 m drop onto a long flat. Cheat: phantom friction (μ 0.25, NOT booked
       as heat) on the flat stops it about halfway along. */
    c2: {
      hills: [{ x: 0, h: 10 }, { x: 3, h: 10 }, { x: 20, h: 0 }, { x: 100, h: 0 }],
      marks: { crest: 3, flat: 20 },
      sim: { m: 500, mu: 0, x0: 3, v0: 0.5, h0: 10, tMax: 12, cheat: { type: 'phantomFriction', x0: 20, x1: 100, mu: 0.25 } },
      clip: { dur: 11, window: R => [R.sim.timeAtX(20) + 1, R.clip.dur] },
    },
    /* c3 HEAVYWEIGHT: the s4 twin drop. Cheat: the elephant's acceleration ×2. */
    c3: {
      track: 'twin',
      sims: {
        max: { m: 50, mu: 0, x0: 4, v0: 0.5, h0: 20, brakes: { x0: 50, x1: 115, mu: 0.8 }, tMax: 12 },
        elephant: { m: 5000, mu: 0, x0: 4, v0: 0.5, h0: 20, brakes: { x0: 50, x1: 115, mu: 0.8 }, tMax: 12, cheat: { type: 'massMatters', factor: 2 } },
      },
      primary: 'elephant',
      clip: { dur: 9, window: R => [0.8, R.sims.elephant.timeAtX(28) + 1] },
    },
    /* c4 THE HONEST ONE: hills 20 / 14 / 9 with friction, an 18 m flat valley, then a 12 m hill
       it can't reach. No cheat. Friction heat is μ·g per metre of x, so the horizontal layout is
       compact and μ = 0.28 (a sticky cartoon track) to fit a 14 s clip: it clears 14 m (5.3 m/s)
       and 9 m (7.1 m/s), climbs the 12 m hill to ~4 m, rolls back and settles on the flat (~13.6 s). */
    c4: {
      hills: [{ x: 0, h: 20 }, { x: 1.65, h: 20 }, { x: 10.45, h: 1 }, { x: 18.15, h: 14 }, { x: 25.3, h: 1 }, { x: 31.9, h: 9 },
        { x: 37.95, h: 1 }, { x: 55.95, h: 1 }, { x: 62.55, h: 12 }, { x: 69.15, h: 2 }, { x: 79.15, h: 2 }],
      marks: { crests: [1.65, 18.15, 31.9], challenge: 62.55, valley: [37.95, 55.95] },
      sim: { m: 500, mu: 0.28, x0: 2.1, h0: 20, tMax: 16 },
      clip: { dur: 14, window: () => null },
    },
    /* c5 LOOP BOOST: 20 m drop into a 12 m vertical loop. Cheat: inside the loop gravity's
       along-track pull is reversed, so it speeds up on the way up and is fastest at the top. */
    c5: {
      hills: [{ x: 0, h: 20 }, { x: 3, h: 20 }, { x: 26, h: 0 }, { x: 40, h: 0, loop: { r: 6, drift: 5 } }, { x: 60, h: 0 }, { x: 110, h: 0 }],
      marks: { crest: 3, loop: 40 },
      sim: { m: 500, mu: 0, x0: 3, v0: 0.5, h0: 20, brakes: { x0: 75, x1: 115, mu: 0.8 }, tMax: 12, cheat: { type: 'loopBoost' } },
      clip: {
        dur: 9,
        window: R => { const a = R.sim.when(p => p.phase >= 0.25); const b = R.sim.when(p => p.phase >= 0.75, a); return [a, b]; },
      },
    },
    /* c6 SPEED TRAP: 5 m drop. Cheat: gravity ×4 along the track, so the cart really moves at
       2× the honest speed and the gauge reads 20 m/s at the bottom (honest: 10 m/s). */
    c6: {
      hills: [{ x: 0, h: 5 }, { x: 3, h: 5 }, { x: 14, h: 0 }, { x: 90, h: 0 }],
      marks: { crest: 3, bottom: 14 },
      sim: { m: 500, mu: 0, x0: 3, v0: 0.5, h0: 5, brakes: { x0: 50, x1: 90, mu: 0.8 }, tMax: 10, cheat: { type: 'wrongGauge', factor: 2 } },
      clip: { dur: 8, window: R => [R.sim.when(p => p.speed >= 11), R.clip.dur] },
    },

    /* honest twins of the cheating clips (the reveal can show "what should have happened") */
    c1_honest: { track: 'c1', sim: { m: 500, mu: 0, x0: 3, v0: 0.5, h0: 20, brakes: { x0: 105, x1: 140, mu: 0.8 }, tMax: 14 } },
    c2_honest: { track: 'c2', sim: { m: 500, mu: 0, x0: 3, v0: 0.5, h0: 10, tMax: 7 } },
    c5_honest: { track: 'c5', sim: { m: 500, mu: 0, x0: 3, v0: 0.5, h0: 20, brakes: { x0: 75, x1: 115, mu: 0.8 }, tMax: 12 } },
    c6_honest: { track: 'c6', sim: { m: 500, mu: 0, x0: 3, v0: 0.5, h0: 5, brakes: { x0: 50, x1: 90, mu: 0.8 }, tMax: 10 } },
  };

  const trackCache = {}, cache = {};
  function trackOf(id) {
    const d = DEFS[id];
    if (d.hills) return trackCache[id] || (trackCache[id] = new C.Track({ hills: d.hills }));
    if (d.track) return trackOf(d.track);
    throw new Error('no track for ' + id);
  }
  function marksOf(id) { const d = DEFS[id]; return Object.assign({}, d.track ? marksOf(d.track) : {}, d.marks || {}); }

  function get(id) {
    if (cache[id]) return cache[id];
    const d = DEFS[id]; if (!d) throw new Error('unknown track config ' + id);
    const track = trackOf(id);
    const R = { id, track, marks: marksOf(id), sims: {} };
    if (d.sim) R.sim = new C.Sim({ track, ...clone(d.sim) });
    if (d.sims) { for (const [k, o] of Object.entries(d.sims)) R.sims[k] = new C.Sim({ track, ...clone(o) }); R.sim = R.sims[d.primary || Object.keys(d.sims)[0]]; }
    if (d.clip) { R.clip = { dur: d.clip.dur, window: null }; R.clip.window = d.clip.window(R); }
    // handy times for scene choreography
    if (id === 's2') R.marks.tTop = R.sim.tTop;
    if (id === 's3') R.marks.tBottom = R.sim.timeAtX(R.marks.bottom);
    if (id === 's4' || id === 'c3') { R.marks.tBottomMax = R.sims.max.timeAtX(28); R.marks.tBottomElephant = R.sims.elephant.timeAtX(28); }
    return (cache[id] = R);
  }
  function clone(o) { return JSON.parse(JSON.stringify(o)); }

  const Tracks = { defs: DEFS, get, ids: Object.keys(DEFS), clips: ['c1', 'c2', 'c3', 'c4', 'c5', 'c6'] };
  if (typeof module !== 'undefined' && module.exports) module.exports = Tracks;
  if (typeof window !== 'undefined') window.Tracks = Tracks;
})(this);
